import { useState } from "react";
import { useGet, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LockedOverlay } from "@/components/LockedOverlay";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Play, Target, Gauge, History, Lock } from "lucide-react";
import { toast } from "sonner";

export function BacktestPage() {
  const { isPro } = useAuth();
  const { data: indData } = useGet("/indicators", 0);
  const [code, setCode] = useState("ALL");
  const [lookback, setLookback] = useState("12");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const indicators = indData?.indicators || [];

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post("/backtest", {
        indicator_code: code === "ALL" ? null : code,
        lookback: Number(lookback),
      });
      setResult(res.data);
      toast.success(`Backtest complete — ${res.data.overall.total_tested} releases replayed`);
    } catch (e) {
      toast.error("Backtest failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isPro) {
    return (
      <div className="relative glass rounded-2xl p-8 min-h-[220px] overflow-hidden">
        <div className="blur-[3px] select-none text-center text-sm text-[#6B7280]">
          <Lock size={28} className="mx-auto mb-3 text-[#6B7280]" />
          Replay the last 6–14 releases per indicator and score the model's direction accuracy
          and average forecast error across the full macro set.
        </div>
        <LockedOverlay label="Unlock Backtesting" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="glass rounded-2xl p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1 space-y-1.5">
          <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">Indicator</label>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger data-testid="backtest-indicator-select" className="bg-[#0A0D14] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10 text-[#F9FAFB] max-h-72">
              <SelectItem value="ALL">All indicators</SelectItem>
              {indicators.map((i) => <SelectItem key={i.code} value={i.code}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-36 space-y-1.5">
          <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">Lookback</label>
          <Select value={lookback} onValueChange={setLookback}>
            <SelectTrigger data-testid="backtest-lookback-select" className="bg-[#0A0D14] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10 text-[#F9FAFB]">
              {["6", "8", "10", "12", "14"].map((n) => <SelectItem key={n} value={n}>{n} releases</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="run-backtest-button" onClick={run} disabled={loading}
          className="bg-[#F59E0B] hover:bg-[#d98c08] text-black font-semibold">
          <Play size={16} className="mr-1.5" /> {loading ? "Running…" : "Run Backtest"}
        </Button>
      </div>

      {!result ? (
        <div className="glass rounded-2xl p-8 text-center text-sm text-[#6B7280]">
          Replay past releases to see how Model A's direction calls and forecast error would have scored.
        </div>
      ) : (
        <div data-testid="backtest-results" className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={Target} label="Dir. Accuracy" value={result.overall.direction_accuracy != null ? `${result.overall.direction_accuracy}%` : "—"} color="#10B981" />
            <Stat icon={Gauge} label="Avg Error" value={result.overall.avg_pct_error != null ? `${result.overall.avg_pct_error}%` : "—"} color="#F59E0B" />
            <Stat icon={History} label="Replayed" value={result.overall.total_tested} color="#6366F1" />
          </div>

          <div className="flex flex-col gap-2">
            {result.results.map((r) => (
              <div key={r.code} className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-semibold text-sm">{r.name}</div>
                  <span className="font-mono-num text-sm font-bold" style={{ color: (r.direction_accuracy || 0) >= 50 ? "#10B981" : "#EF4444" }}>
                    {r.direction_accuracy}%
                  </span>
                </div>
                <Progress value={r.direction_accuracy} className="h-1.5 bg-white/10" />
                <div className="flex justify-between text-[10px] text-[#6B7280] font-mono-num mt-2">
                  <span>{r.correct}/{r.tested} correct</span>
                  <span>avg error {r.avg_pct_error}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="glass rounded-2xl p-4 text-center">
      <Icon size={18} className="mx-auto mb-1" style={{ color }} />
      <div className="font-mono-num text-xl font-bold">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
    </div>
  );
}
